import { BaseApi, Environment } from '@nextroll/ar-base-api';
import { WorkflowPostBody } from 'components/pages/CanvasPage/CanvasPage';

import { IWorkflow, IWorkflowStats, WorkflowStatusType } from 'types/rofl';

import { IWorkflowTemplate } from 'types/templates';

const workflowsApi = new BaseApi('/rofl-api/v1/', {
    environment: (process.env.ENVIRONMENT || 'development') as Environment,
});

const getRequestUrl = (advertisableEid: string, workflowEid?: string) => [
    `workflows${workflowEid ? `/${workflowEid}` : ''}`,
    { advertisable_eid: advertisableEid },
] as const;

/**
 * Get Workflow by given EID
 */

export const getWorkflow = (workflowEid: string, advertisableEid: string): Promise<IWorkflow> => {
    return workflowsApi.get(
        ...getRequestUrl(advertisableEid, workflowEid)).then(res => res.result || {} as IWorkflow)
        .catch(error => {
            throw new Error(error);
        });
};

/**
 * Get the list of existing workflows.
 * @param advertisableEid  The user's associated advertisable EID.
 */
export const getWorkflowList = async (advertisableEid: string): Promise<IWorkflow[]> => {
    const [
        { result: workflows = [] }, // basic workflow info
        { result: workflowStats = [] }, // workflow stat info
    ] = await Promise.all([
        workflowsApi.get(...getRequestUrl(advertisableEid)),
        workflowsApi.get(...getRequestUrl(advertisableEid, 'enrollments/stats')),
    ]) as [{ result: IWorkflow[] }, { result: IWorkflowStats[] }];

    // no workflows; skip all the preparation logic
    if (workflows.length <= 0) {
        return [];
    }

    // translate the stat array into a lookup map of workflow EIDs as keys
    const statLookupMap = workflowStats.reduce((map, stat) => ({
        ...map,
        [stat.workflow_eid]: stat,
    }), {}) as { [eid: string]: IWorkflowStats };

    // combine basic workflow info + stat together
    return workflows.map((workflow: IWorkflow) => ({
        ...workflow,
        ...statLookupMap[workflow.eid],
    }));
};

/**
 * Changes the workflow's status from on to off and vice versa.
 * @param workflowEid The workflow EID.
 * @param advertisableEid  The user's associated advertisable EID.
 * @param updatedStatus The workflow's updated status.
 */
export const toggleWorkflowStatus = (
    workflowEid: string,
    advertisableEid: string,
    updatedStatus: WorkflowStatusType
) => workflowsApi.patch(...getRequestUrl(advertisableEid, workflowEid), { status: updatedStatus });

/**
 * Delete a specific workflow of the given ID.
 * @param workflowEid The workflow EID.
 * @param advertisableEid  The user's associated advertisable EID.
 */
export const deleteWorkflow = (
    workflowEid: string,
    advertisableEid: string
) => workflowsApi.delete(...getRequestUrl(advertisableEid, workflowEid));

/**
 * Creates a New Workflow
 * @param advertisableEid The user's associated advertisable EID.
 * @param postBody The json body used in the POST request to create a new the workflow.
 */
export const createWorkflow = async (
    advertisableEid: string,
    postBody: WorkflowPostBody,
) => workflowsApi.post(...getRequestUrl(advertisableEid), postBody);

/**
 * Updates an existing Workflow
 * @param workflowEid The workflow EID.
 * @param advertisableEid The user's associated advertisable EID.
 * @param postBody The json body used in the PUT request to update a workflow.
 */
export const updateWorkflow = async (
    workflowEid: string,
    advertisableEid: string,
    postBody: WorkflowPostBody,
) => workflowsApi.put(...getRequestUrl(advertisableEid, workflowEid), postBody);

/**
 * Validates an existing Workflow
 * @param workflowEid The workflow EID.
 * @param advertisableEid The user's associated advertisable EID.
*/
export const validateWorkflow = async (
    workflowEid: string,
    advertisableEid: string,
) => {
    const url = `workflows/${workflowEid}/validate`;
    const params = {
        advertisable_eid: advertisableEid,
    };
    // Use makeRequest directly because post has an empty JSON body by default and this endpoint
    // requires passing no body at all in order to pick up the schema from the workflow automatically.
    return workflowsApi.makeRequest(
        workflowsApi.getEndpoint(url, params),
        'POST',
    ).then(res => res.result || {});
};

/**
 * Return the list of workflow templates available.
 */
export const getWorkflowTemplates = async (
    advertisableEid: string,
    bu?: string,
): Promise<IWorkflowTemplate[]> => {
    const response = await workflowsApi.get('workflow-templates', {
        advertisable_eid: advertisableEid,
        ...(bu ? { bu } : {}),
    }) as {result: IWorkflowTemplate[]};
    return (response.result || []);
};

/**
 * Return the filtered contacts for the contact discovery feature.
 */
export interface IContactByFilterContact {
    key?: string; // for rendering the preview table
    company_domain: string;
    first_name: string;
    last_name: string;
    job_title: string;
    has_mobile_number: boolean;
    has_direct_number: boolean;
    has_email: boolean;
    linkedin_url: string;
}
export interface IContactByFilterContacts {
    contacts?: IContactByFilterContact[];
    next?: string;
}
export const getContactsByFilter = async (
    advertisableEid?: string,
    crm?: 'salesforce' | 'hubspot' | 'marketo',
    body?: any,
    next?: string,
    signal?: AbortSignal,
) => {
    if (!advertisableEid || !crm || !body) {
        return {}; // data is not ready; do nothing
    }
    const response = await workflowsApi.post(
        'contact-discovery/get',
        { advertisable_eid: advertisableEid, crm, count: 100, ...(next ? { continue: next } : {}) },
        body,
        signal,
    ) as { results: IContactByFilterContact[]; continue: string };
    return {
        contacts: response.results || [],
        next: response.continue,
    };
};

export default workflowsApi;
